import React from 'react';
import { Section } from './ui/Section';
import { FadeIn } from './ui/FadeIn';
import { CheckCircle2, Clock, TrendingUp } from 'lucide-react';
export function CaseStudy() {
  return <Section background="ivory" className="relative overflow-hidden bg-purple-50" data-id="element-57">
      {/* Decorative background element */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-purple-100/30 rounded-full blur-3xl -ml-48 -mt-48" data-id="element-58"></div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center relative z-10" data-id="element-59">
        {/* Left: Story */}
        <div className="order-2 lg:order-1" data-id="element-60">
          <FadeIn data-id="element-61">
            <div className="text-sm uppercase tracking-widest text-purple-600 font-semibold mb-6" data-id="element-62">
              Case Study
            </div>
            <h2 className="text-4xl md:text-5xl font-serif text-charcoal mb-8 leading-tight" data-id="element-63">
              Meet Casie
            </h2>
          </FadeIn>

          <div className="space-y-6 text-lg text-charcoal/80 leading-relaxed" data-id="element-64">
            <FadeIn delay={200} data-id="element-65">
              <p data-id="element-66">
                Casie is a high-performing executive. On paper, her life was
                thriving — leadership role, growing team, constant momentum.
              </p>
            </FadeIn>

            <FadeIn delay={300} data-id="element-67">
              <p data-id="element-68">
                But behind the scenes, she was dealing with persistent fatigue,
                anxiety, and a sense that something wasn't quite right.
              </p>
            </FadeIn>

            <FadeIn delay={400} data-id="element-69">
              <p data-id="element-70">
                She saw multiple providers, waited weeks between appointments,
                and received fragmented, often conflicting advice. Nothing felt
                truly personalized — or complete.
              </p>
            </FadeIn>

            <FadeIn delay={500} data-id="element-71">
              <p className="font-medium text-charcoal" data-id="element-72">
                Over time, she began to lose trust in the system.
              </p>
            </FadeIn>

            <FadeIn delay={600} data-id="element-73">
              <div className="pt-6 border-t border-charcoal/10" data-id="element-74">
                <p className="text-2xl font-serif text-charcoal mb-4" data-id="element-75">
                  Then she found Fem Entity.
                </p>
                <p data-id="element-76">
                  Within minutes, Casie was guided through a personalized intake
                  and matched with a curated network of trusted practitioners.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={700} data-id="element-77">
              <p className="italic" data-id="element-78">
                For the first time, her care felt connected.
              </p>
            </FadeIn>

            <FadeIn delay={800} data-id="element-79">
              <p data-id="element-80">
                Her wellness plan adapted to her needs — integrating insights,
                tracking progress, and aligning her providers within one
                ecosystem.
              </p>
            </FadeIn>
          </div>
        </div>

        {/* Right: Visual + Results */}
        <div className="order-1 lg:order-2 space-y-8" data-id="element-81">
          <FadeIn delay={200} data-id="element-82">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-charcoal/10" data-id="element-83">
              <img src="https://cdn.magicpatterns.com/uploads/czmN7x1t4juHwMujJGpE9F/pexels-rdne-5778469.jpg" alt="Professional woman working" className="w-full h-[400px] object-cover" data-id="element-84" />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 to-transparent" data-id="element-85"></div>
            </div>
          </FadeIn>

          <FadeIn delay={400} data-id="element-86">
            <div className="glass-panel rounded-2xl p-8 space-y-6" data-id="element-87">
              <div className="flex items-center space-x-3 pb-4 border-b border-charcoal/10" data-id="element-88">
                <Clock className="w-5 h-5 text-purple-700" data-id="element-89" />
                <h3 className="text-xl font-serif text-charcoal" data-id="element-90">
                  Within 90 Days
                </h3>
              </div>

              <div className="space-y-4" data-id="element-91">
                <div className="flex items-start space-x-3" data-id="element-92">
                  <CheckCircle2 className="w-5 h-5 text-purple-600 mt-1 flex-shrink-0" data-id="element-93" />
                  <div data-id="element-94">
                    <div className="font-medium text-charcoal" data-id="element-95">
                      Her energy stabilized
                    </div>
                    <div className="text-sm text-charcoal/60" data-id="element-96">
                      Consistent, sustainable vitality
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-3" data-id="element-97">
                  <CheckCircle2 className="w-5 h-5 text-purple-600 mt-1 flex-shrink-0" data-id="element-98" />
                  <div data-id="element-99">
                    <div className="font-medium text-charcoal" data-id="element-100">
                      Her anxiety decreased
                    </div>
                    <div className="text-sm text-charcoal/60" data-id="element-101">
                      Measurable reduction in stress markers
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-3" data-id="element-102">
                  <CheckCircle2 className="w-5 h-5 text-purple-600 mt-1 flex-shrink-0" data-id="element-103" />
                  <div data-id="element-104">
                    <div className="font-medium text-charcoal" data-id="element-105">
                      Her care became clear and consistent
                    </div>
                    <div className="text-sm text-charcoal/60" data-id="element-106">
                      Unified, coordinated approach
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={600} data-id="element-107">
            <div className="bg-charcoal text-ivory p-8 rounded-2xl" data-id="element-108">
              <div className="flex items-start space-x-4" data-id="element-109">
                <TrendingUp className="w-6 h-6 text-purple-400 mt-1 flex-shrink-0" data-id="element-110" />
                <div data-id="element-111">
                  <p className="text-lg font-serif mb-3" data-id="element-112">
                    Today, Casie understands her health — and feels in control
                    of it.
                  </p>
                  <p className="text-ivory/70 text-sm leading-relaxed" data-id="element-113">
                    Fem Entity didn't just give her access. It gave her clarity,
                    confidence, and continuity of care.
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </Section>;
}